import { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import MagazinePageHeader from './MagazinePageHeader';
import MagazineNewButton from './MagazineNewButton';
import MagazineFormModal from './MagazineFormModal';
import { useAuth } from '../../hooks/useAuth';
import {
  createPublication,
  deletePublication,
  getPublications,
  getPublicationLogoUrl,
  updatePublication,
} from '../../api/magazineService';

const emptyForm = { name: '', description: '', status: 'active' };

const fieldClass =
  'mt-1.5 box-border w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-[#5949d1] focus:ring-2 focus:ring-[#5949d1]/10';
const labelClass = 'block text-[13px] font-semibold text-gray-700';

const Publications = () => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logo, setLogo] = useState<File | null>(null);

  const load = async () => {
    setLoading(true);
    const data = await getPublications();
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const closeForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setLogo(null);
    setShowForm(false);
  };

  const openNewForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setLogo(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    const data = new FormData();
    data.append('name', form.name);
    data.append('description', form.description);
    data.append('status', form.status);
    if (logo) data.append('logo', logo);
    if (editingId) {
      await updatePublication(editingId, data);
    } else {
      await createPublication(data);
    }
    closeForm();
    load();
  };

  const handleEdit = (item: any) => {
    setEditingId(item.id);
    setLogo(null);
    setForm({
      name: item.name,
      description: item.description || '',
      status: item.status || 'active',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin || !window.confirm('Delete this publication?')) return;
    await deletePublication(id);
    load();
  };

  return (
    <Layout>
      <MagazinePageHeader
        title="Publications"
        description="Create and manage magazine publications available for booking."
        action={isAdmin ? <MagazineNewButton onClick={openNewForm} /> : undefined}
      />

      {isAdmin && (
        <MagazineFormModal
          open={showForm}
          title={editingId ? 'Edit publication' : 'New publication'}
          onClose={closeForm}
        >
          <form onSubmit={handleSubmit} className="grid min-w-0 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="publication-name" className={labelClass}>Name</label>
              <input
                id="publication-name"
                className={fieldClass}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="publication-status" className={labelClass}>Status</label>
              <select
                id="publication-status"
                className={fieldClass}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="min-w-0 sm:col-span-2">
              <label htmlFor="publication-description" className={labelClass}>Description</label>
              <textarea
                id="publication-description"
                className={`${fieldClass} min-w-0 resize-y`}
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="min-w-0 sm:col-span-2">
              <label htmlFor="publication-logo" className={labelClass}>Publication logo</label>
              <input
                id="publication-logo"
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className={fieldClass}
                onChange={(e) => setLogo(e.target.files?.[0] || null)}
              />
              <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF or WebP, up to 5 MB. Leave empty to keep the current logo.</p>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <button
                type="submit"
                className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark"
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </MagazineFormModal>
      )}

      <PageCard>
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No publications yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Logo</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Description</th>
                  {isAdmin && <th className="py-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-medium text-gray-900">{item.name}</td>
                    <td className="py-3 pr-4">
                      {item.logo_path ? <img src={getPublicationLogoUrl(item.logo_path)} alt={`${item.name} logo`} className="h-10 w-24 object-contain object-left" /> : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-3 pr-4 capitalize text-gray-600">{item.status}</td>
                    <td className="py-3 pr-4 text-gray-600">{item.description || '—'}</td>
                    {isAdmin && (
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-sm text-torch-primary hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-sm text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default Publications;
